import { BUILD_ID } from "../buildId";

/**
 * Auto-reload when the running bundle is stale.
 *
 * The drawer is embedded as a long-lived iframe/widget on retailer pages
 * (PDPs, SPAs, kiosks). Such instances can keep an old JS bundle in memory
 * for days without ever doing a full reload — so even with `max-age=0` on
 * index.html, old builds keep running in production and emit bugs already
 * fixed in newer builds (e.g. missing i18n keys rendering raw).
 *
 * On boot we fetch `/version.json` (served no-store) and, if the deployed
 * build differs from ours, do a one-shot reload to pick up the fresh bundle.
 * A per-target sessionStorage guard prevents reload loops if propagation
 * lags. This only helps from this build onward (older builds lack the check),
 * but it stops the long stale-build tail from recurring after future deploys.
 */
export async function checkBuildFreshness(): Promise<void> {
  // Never reload in local/dev (BUILD_ID is the committed placeholder).
  if (!BUILD_ID || BUILD_ID === "dev") return;

  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return;

    const data = (await res.json()) as { build?: string };
    const latest = data?.build;
    if (!latest || latest === BUILD_ID) return;

    // Stale build → reload once per target build (loop-safe).
    const guardKey = `kleep_reloaded_for_${latest}`;
    try {
      if (sessionStorage.getItem(guardKey)) return;
      sessionStorage.setItem(guardKey, "1");
    } catch {
      // sessionStorage unavailable (sandboxed iframe): a stale build that
      // can't set the guard would risk a loop, so bail rather than reload.
      return;
    }

    window.location.reload();
  } catch {
    // Network error / offline / blocked: leave the current bundle running.
  }
}
